import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, RefreshCw } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { DEFAVORABLE, FAVORABLE, NEUTRE_DOUX } from '../../lib/couleurs';
import { candidatsTransaction, rejeterTransaction, validerRapprochement } from '../../lib/passerelle';
import { Carte, btnLight, td, th } from '../cri/ui';

const mil = (v) => (v == null ? '—' : fmt(v / 1000, 2));

const Ligne = ({ tx, onMaj }) => {
  const [cands, setCands] = useState(null);

  const charger = async () => setCands((await candidatsTransaction(tx._id))?.candidats ?? []);

  const valider = async (payment_id) => {
    const { error, data } = await validerRapprochement(tx._id, payment_id);
    if (error) return toast.error(error);
    toast.success(`Règlement confirmé pour ${mil(data.montant_millimes)} TND${data.commission_millimes ? ` (commission séparée : ${mil(data.commission_millimes)} TND)` : ''}.`);
    onMaj();
  };

  const rejeter = async () => {
    const { error } = await rejeterTransaction(tx._id, 'rejet bancaire');
    if (error) return toast.error(error);
    toast.success('Rejet enregistré : règlement annulé et dette recalculée.');
    onMaj();
  };

  return (
    <>
      <tr data-testid={`passerelle-tx-${tx._id}`} className="hover:bg-slate-50">
        <td className={td}>{tx.date_operation ?? '—'}</td>
        <td className={td}>{tx.libelle ?? '—'}</td>
        <td className={`${td} ${NEUTRE_DOUX}`}>{tx.sens}</td>
        <td className={`${td} font-semibold ${tx.sens === 'debit' ? DEFAVORABLE : FAVORABLE}`}>{mil(tx.montant_millimes)}</td>
        <td className={td}>{mil(tx.affecte_millimes)}</td>
        <td className={td}>{tx.commission_millimes ? mil(tx.commission_millimes) : '—'}</td>
        <td className={`${td} ${NEUTRE_DOUX}`}>{tx.reference_bancaire ?? '—'}</td>
        <td className="px-3 py-2">
          <span
            className={`border px-1.5 py-0.5 font-data text-[11px] uppercase ${
              tx.etat === 'rapproche'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : tx.etat === 'rejete'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-amber-300 bg-amber-50 text-amber-800'
            }`}
          >
            {tx.etat}
          </span>
        </td>
        <td className="px-3 py-2">
          <div className="flex gap-1">
            <button data-testid={`passerelle-cands-${tx._id}`} type="button" onClick={charger} className={btnLight}>
              <RefreshCw size={12} /> Candidats
            </button>
            {tx.etat === 'rapproche' && (
              <button data-testid={`passerelle-rejet-${tx._id}`} type="button" onClick={rejeter} className={btnLight}>
                Rejet
              </button>
            )}
          </div>
        </td>
      </tr>
      {cands && (
        <tr className="bg-slate-50">
          <td colSpan={9} className="px-4 py-2">
            {cands.length === 0 ? (
              <p className="text-xs text-slate-500">Aucun règlement candidat : cette opération reste non rapprochée.</p>
            ) : (
              <div className="space-y-1">
                {cands.map((c) => (
                  <div key={c.payment_id} className="flex items-center justify-between border border-slate-200 bg-white px-2 py-1.5">
                    <span className="font-data text-xs text-slate-700">
                      {c.payment_id} · {mil(c.montant_millimes)} TND · {c.methode} (score {c.score})
                    </span>
                    <button data-testid={`passerelle-valider-${c.payment_id}`} type="button" onClick={() => valider(c.payment_id)} className={btnLight}>
                      <Check size={12} /> Confirmer
                    </button>
                  </div>
                ))}
                <p className="text-[11px] text-slate-500">
                  Le score classe les candidats ; il ne justifie jamais seul une validation. Aucun écart n’est absorbé dans une tolérance.
                </p>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

export const BanquePasserelle = ({ onMaj }) => {
  const [data, setData] = useState({ transactions: [], extraits: [] });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    import('../../lib/passerelle').then(({ fetchRapprochements }) => fetchRapprochements().then(setData));
  }, [tick]);

  const maj = () => {
    setTick((t) => t + 1);
    onMaj();
  };

  return (
    <div className="space-y-4">
      <Carte testid="passerelle-extraits" titre="Extraits importés" sous="Observations documentaires et contrôle solde initial + crédits − débits = solde final. Un solde absent reste inconnu.">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                <th className={th}>Compte</th>
                <th className={th}>Période</th>
                <th className={th}>Solde initial</th>
                <th className={th}>Crédits</th>
                <th className={th}>Débits</th>
                <th className={th}>Solde final</th>
                <th className={th}>Contrôle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.extraits.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={7}>
                    Aucun extrait bancaire importé par la passerelle.
                  </td>
                </tr>
              )}
              {data.extraits.map((s) => (
                <tr key={s._id} data-testid={`passerelle-extrait-${s._id}`}>
                  <td className={td}>{s.compte_id}</td>
                  <td className={td}>
                    {s.periode?.debut ?? '—'} → {s.periode?.fin ?? '—'}
                  </td>
                  <td className={td}>{mil(s.solde_initial_millimes)}</td>
                  <td className={td}>{mil(s.credits_millimes)}</td>
                  <td className={td}>{mil(s.debits_millimes)}</td>
                  <td className={td}>{mil(s.solde_final_millimes)}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`border px-1.5 py-0.5 font-data text-[11px] uppercase ${
                        s.controle_soldes?.etat === 'ok'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : s.controle_soldes?.etat === 'echec'
                            ? 'border-red-200 bg-red-50 text-red-700'
                            : 'border-slate-300 bg-slate-50 text-slate-600'
                      }`}
                    >
                      {s.controle_soldes?.etat ?? '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>

      <Carte
        testid="passerelle-transactions"
        titre="Transactions bancaires canoniques"
        sous="Une opération peut apparaître dans plusieurs extraits sans être dupliquée ; deux débits réels identiques restent deux transactions."
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1040px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                <th className={th}>Date</th>
                <th className={th}>Libellé</th>
                <th className={th}>Sens</th>
                <th className={th}>Montant</th>
                <th className={th}>Affecté</th>
                <th className={th}>Commission</th>
                <th className={th}>Référence</th>
                <th className={th}>État</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.transactions.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={9}>
                    Aucune transaction bancaire.
                  </td>
                </tr>
              )}
              {data.transactions.map((tx) => (
                <Ligne key={tx._id} tx={tx} onMaj={maj} />
              ))}
            </tbody>
          </table>
        </div>
      </Carte>
    </div>
  );
};
